// Minimal manual "Link to loan" control for the lead detail drawer (PR-8).
//
// Attribution ROI / conversion / time-to-close all read the lead's
// linkedLoanId; with 0 leads linked today those reports are structurally 0.
// This is the manual entry point — the operator pastes/types the loan id and
// links it. The backend (leadAction.linkLeadToLoan) validates the loan exists
// in raw_commissions for the company before stamping Firestore + BQ. The
// auto-reconciler is deferred.
import React, { useEffect, useState } from 'react';
import { Link2, Link2Off, RefreshCw, Vault } from 'lucide-react';

export const LinkLeadToLoanControl: React.FC<{
  linkedLoanId: string | null;
  onLink: (loanId: string | null) => Promise<{ linkedLoanId: string | null }>;
}> = ({ linkedLoanId, onLink }) => {
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset the input whenever the linked loan changes (e.g. drawer switches
  // leads, or a link/unlink completes and the parent re-renders with new data).
  useEffect(() => {
    setValue('');
    setError(null);
  }, [linkedLoanId]);

  const handleLink = async () => {
    const loanId = value.trim();
    if (!loanId) return;
    setBusy(true);
    setError(null);
    try {
      await onLink(loanId);
      setValue('');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const handleUnlink = async () => {
    setBusy(true);
    setError(null);
    try {
      await onLink(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1 inline-flex items-center gap-1">
        <Vault className="w-3 h-3" />
        Linked loan
      </div>

      {linkedLoanId ? (
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-mono text-emerald-300 break-all">
            {linkedLoanId}
          </span>
          <button
            onClick={handleUnlink}
            disabled={busy}
            title="Unlink this loan"
            className="px-2 py-1 rounded-md bg-gray-700 hover:bg-gray-600 text-gray-200 text-xs inline-flex items-center gap-1 disabled:opacity-50"
          >
            {busy ? (
              <RefreshCw className="w-3 h-3 animate-spin" />
            ) : (
              <Link2Off className="w-3 h-3" />
            )}
            Unlink
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleLink();
            }}
            placeholder="Loan ID (from commission data)"
            className="flex-1 px-3 py-1.5 rounded-md bg-gray-800 border border-gray-700 text-white text-sm font-mono focus:outline-none focus:border-blue-500"
          />
          <button
            onClick={handleLink}
            disabled={busy || !value.trim()}
            className="px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-xs inline-flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy ? (
              <RefreshCw className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Link2 className="w-3.5 h-3.5" />
            )}
            Link
          </button>
        </div>
      )}

      {error && <p className="text-xs text-red-400 mt-1">{error}</p>}
      <p className="text-[11px] text-gray-500 mt-1">
        Links this lead to a settled loan so it counts toward Attribution ROI
        and conversion reporting.
      </p>
    </div>
  );
};

export default LinkLeadToLoanControl;
